import type { ComponentType, SVGProps } from 'react';

export type SpookyIconProps = SVGProps<SVGSVGElement> & { size?: number };
export type SpookyIconComponent = ComponentType<SpookyIconProps>;

const IconFrame = ({ children, size = 24, ...props }: SpookyIconProps) => (
  <svg
    viewBox="0 0 48 48"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
);

export const GhostIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M12 38V22C12 13.7 17.2 8 24 8s12 5.7 12 14v16l-5-3.4-4.7 4-4.8-4-4.5 3.8L12 38Z" />
    <path d="M19 21h.1M29 21h.1" />
    <path d="M21 28c1.7-1.5 4.3-1.5 6 0" />
  </IconFrame>
);

export const BatIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M24 19c-3.5-4-7-6-11.5-7.5l2.2 7.1L7 20.5l7.8 4.1L12 32c5-1.1 8.9 1 12 6 3.1-5 7-7.1 12-6l-2.8-7.4 7.8-4.1-7.7-1.9 2.2-7.1C31 13 27.5 15 24 19Z" />
    <path d="M21.5 18.5 24 15l2.5 3.5" />
  </IconFrame>
);

export const PumpkinIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M24 12c.2-3 1.5-5 4-6" />
    <path d="M24 11c-9 0-15 5.8-15 14.5S15 41 24 41s15-6.8 15-15.5S33 11 24 11Z" />
    <path d="M24 12c-4 3.4-5 8-5 13.5S20 37.6 24 41M24 12c4 3.4 5 8 5 13.5S28 37.6 24 41" />
    <path d="m15.5 24 4-3 2 4h-6ZM32.5 24l-4-3-2 4h6ZM17 31c4.8 3.5 9.2 3.5 14 0" />
  </IconFrame>
);

export const CalendarPumpkinIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M8 12h32v28H8zM8 19h32M15 8v8M33 8v8" />
    <path d="M14 26h4M23 26h4M14 33h4M23 33h4" />
    <path d="M34 29c-4 0-6.5 2.7-6.5 6s2.5 6 6.5 6 6.5-2.7 6.5-6-2.5-6-6.5-6Z" fill="var(--color-surface)" />
    <path d="M34 29c0-1.6.8-2.5 2-3M30.8 35h.1M37.2 35h.1M31.5 38c1.7 1 3.3 1 5 0" />
  </IconFrame>
);

export const ClipboardBatIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M17 10h-5v32h24V10h-5M18 7h12v7H18z" />
    <path d="m16 23 2.5 2.5L23 21M16 32l2.5 2.5L23 30" />
    <path d="M28 24c2-2 4-3 7-3l-1.4 3.2 4.4.8-4 2.3-2 3.7-2-3.7-4-2.3 4.4-.8L28 24Z" />
  </IconFrame>
);

export const NoteGhostIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M10 7h24v34H10zM16 15h12M16 21h12M16 27h7" />
    <path d="M28 40V29c0-5 3.2-8 7-8s7 3 7 8v11l-3-2.3-2.5 2.5-2.8-2.5-2.7 2.4L28 40Z" fill="var(--color-surface)" />
    <path d="M33 29h.1M38 29h.1" />
  </IconFrame>
);

export const SkullGearIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="m24 5 3 3.5 4.5-1 1 4.5 4.5 1-1 4.5 3.5 3-3.5 3 1 4.5-4.5 1-1 4.5-4.5-1L24 37l-3-3.5-4.5 1-1-4.5-4.5-1 1-4.5-3.5-3 3.5-3-1-4.5 4.5-1 1-4.5 4.5 1L24 5Z" />
    <path d="M18 20c0-4 2.4-6.5 6-6.5S30 16 30 20c0 2.8-1.2 4.5-3 5.5V29h-6v-3.5c-1.8-1-3-2.7-3-5.5Z" />
    <path d="M21.5 20h.1M26.5 20h.1M23 25h2" />
  </IconFrame>
);

export const BroomIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="m35 6-17 24" />
    <path d="m16 27 7 5-8 10-11-8 12-7Z" />
    <path d="m8 32 10 7M12 29l9 7" />
  </IconFrame>
);

export const CauldronIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="M10 20h28c0 12-5 19-14 19S10 32 10 20ZM8 17h32M15 39l-2 4M33 39l2 4" />
    <path d="M19 11c0-2 1.2-3 3-3M27 13c0-2 1.2-3 3-3M24 6c0-1.5 1-2.5 2.5-2.5" />
  </IconFrame>
);

export const CatIcon: SpookyIconComponent = (props) => (
  <IconFrame {...props}>
    <path d="m15 16-2-8 7 4a14 14 0 0 1 8 0l7-4-2 8c2 2.4 3 5.2 3 8.5C36 33 31 39 24 39s-12-6-12-14.5c0-3.3 1-6.1 3-8.5Z" />
    <path d="M19 23h.1M29 23h.1M22 28h4M24 28v3M19 30l-5 1M29 30l5 1" />
  </IconFrame>
);
